// PotentialCollabsDashboardView.js
import React, { useState, useEffect } from 'react';
import {
  ActivityIndicator,
  SafeAreaView,
  StyleSheet,
  View,
  Text,
  ScrollView,
  Linking,
  Image,
  TextInput,
  TouchableOpacity,
  Modal, // kept as in original (even if unused)
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { differenceInCalendarDays } from 'date-fns';
import { LinearGradient } from 'expo-linear-gradient';

const PotentialCollabsDashboardView = ({ navigation }) => {
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [userData, setUserData] = useState({});
  const [noOffersMessage, setNoOffersMessage] = useState('');

  const [adminRAGData, setAdminRAGData] = useState([]);
  const [loadingRAG, setLoadingRAG] = useState(true);

  const [loading, setLoading] = useState(true);

  // ✅ NEW: subscription / auth state for sidenav lock + CTA
  const [isAuthed, setIsAuthed] = useState(false);
  const [authMode, setAuthMode] = useState('none'); // "subscriber" | "none"
  const [authChecked, setAuthChecked] = useState(false);
  const isSubscriber = authMode === 'subscriber';

  const IMAGE_BASE = 'https://collabbayadmin.com/listings/';

  const getCoverImage = (logoCsv) => {
    if (!logoCsv || !logoCsv.trim()) {
      return 'https://collabbayadmin.com/NoListingImage.jpg';
    }
    const first = logoCsv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)[0];
    if (!first) return 'https://collabbayadmin.com/NoListingImage.jpg';

    const url = first.startsWith('http') ? first : IMAGE_BASE + first;
    return `${url}?t=${Date.now()}`; // cache-bust
  };

  const hasAnyImage = (logoCsv) =>
    !!(
      logoCsv &&
      logoCsv
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean).length
    );

  const fetchAdminRAGData = async () => {
    try {
      setLoadingRAG(true);
      const response = await fetch(
        'https://collabbayadmin.com/APIControllers/BrandGetContentRAG.php'
      );
      const data = await response.json();

      if (data.success) {
        const filteredData = data.results.filter(
          (collab) => collab.Status === 'YELLOW' || collab.Status === 'RED'
        );
        setAdminRAGData(filteredData);
      } else {
        setAdminRAGData([]);
      }
    } catch (error) {
      console.error('Error fetching Admin RAG data:', error);
      setAdminRAGData([]);
    } finally {
      setLoadingRAG(false);
    }
  };

  const fetchData = async () => {
    try {
      const response = await fetch(
        'https://collabbayadmin.com/APIControllers/RetrieveBrandListings.php'
      );
      const jsonData = await response.json();

      if (jsonData.message) {
        setNoOffersMessage(jsonData.message);
        setData([]);
      } else if (Array.isArray(jsonData)) {
        setData(jsonData);
        setNoOffersMessage('');
      } else {
        setData([]);
        setNoOffersMessage('');
      }
    } catch (error) {
      console.error('Error fetching data: ', error);
      setNoOffersMessage('Error fetching data');
    }
  };

  const fetchUserData = () => {
    fetch('https://collabbayadmin.com/APIControllers/GetSessionData.php')
      .then((response) => response.json())
      .then((data) => setUserData(data))
      .catch((error) => {
        console.error('Error fetching user data: ', error);
      });
  };

  // ✅ NEW: fetch subscription/auth state for sidenav lock + CTA
  useEffect(() => {
    fetch(
      'https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php',
      {
        method: 'GET',
        credentials: 'include',
      }
    )
      .then((res) => res.json())
      .then((data) => {
        const authed =
          typeof data.authenticated === 'boolean'
            ? data.authenticated
            : data.status === 'active';
        const mode = data.auth_mode
          ? data.auth_mode
          : data.status === 'active'
          ? 'subscriber'
          : 'none';

        setIsAuthed(!!authed);
        setAuthMode(mode);
      })
      .catch((err) => console.error('Error fetching auth state:', err))
      .finally(() => setAuthChecked(true));
  }, []);

  // ✅ NEW: open link in same tab (web) or via Linking (native)
  const openInSameTab = (url) => {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    } else {
      Linking.openURL(url);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      await Promise.all([fetchData(), fetchUserData(), fetchAdminRAGData()]);
      setLoading(false);
    };
    fetchAllData();
  }, []);

  // keep extra effects as in original
  useEffect(() => {
    fetchAdminRAGData();
  }, []);

  useEffect(() => {
    fetchData();
    fetchUserData();
  }, []);

  const handleBackPress = () => {
    navigation.goBack();
  };

  const handleSearchChange = (text) => {
    setSearchQuery(text);
  };

  const filteredData = data.filter((item) =>
    item.AdHeader.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();

    const suffix =
      day % 10 === 1 && day !== 11
        ? 'st'
        : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
        ? 'rd'
        : 'th';

    const formattedDate = `${day}${suffix} ${month}`;
    return year !== now.getFullYear()
      ? `${formattedDate}, ${year}`
      : formattedDate;
  };

  // ⏳ LOADING STATE (NOW WITH SIDENAV)
  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.appShell}>
          <View style={styles.shellRow}>
            {/* ✅ SIDENAV */}
            <View style={styles.sideNav}>
              <LinearGradient
                colors={['#050509', '#050505', '#000000']}
                style={styles.sideNavInner}
              >
                <LinearGradient
                  colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
                  style={styles.sideNavGlow}
                  start={{ x: 0.1, y: 0 }}
                  end={{ x: 1, y: 1 }}
                />

                <View style={styles.sideNavHeader}>
                  <Image
                    source={{
                      uri: 'https://collabbayadmin.com/CollabbayLogoDark.png',
                    }}
                    style={styles.sideNavLogoImage}
                    resizeMode="contain"
                  />
                  <View>
                    <Text style={styles.logoWordmark}>
                      <Text style={{ fontWeight: '300' }}>collab</Text>
                      bay
                      <Text style={styles.logoDot}>.</Text>
                    </Text>
                    <Text style={styles.logoDesktopTag}>DESKTOP</Text>
                  </View>
                </View>

                <View style={styles.sideNavSection}>
                  <LinearGradient
                    colors={['#111111', '#2b2b2b']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={styles.usernamePill}
                  >
                    <Text
                      numberOfLines={1}
                      ellipsizeMode="tail"
                      style={styles.usernamePillText}
                    >
                      {userData.Username ? ` ${userData.Username}` : 'Welcome Back'}
                    </Text>
                  </LinearGradient>

                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => navigation.navigate('MyPanel')}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons name="home-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                        <Text style={styles.navText}>Home</Text>
                      </View>
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => navigation.navigate('MyOffers')}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons name="pricetags-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                        <Text style={styles.navText}>My Offers</Text>
                      </View>
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => {
                      if (authChecked && !isAuthed) openInSameTab('https://collabbayadmin.com/subscribe');
                      else navigation.navigate('MyCalendar');
                    }}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons name="calendar-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                        <Text style={styles.navText}>My Calendar</Text>
                      </View>
                      {authChecked && !isAuthed && (
                        <Ionicons name="lock-closed-outline" size={16} color="#FDF05D" style={styles.lockIcon} />
                      )}
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => navigation.navigate('BrowseCreators')}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons name="people-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                        <Text style={styles.navText}>Browse Creators</Text>
                      </View>
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => {
                      if (authChecked && !isAuthed) openInSameTab('https://collabbayadmin.com/subscribe');
                      else navigation.navigate('MyActivityFeed');
                    }}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons name="chatbubbles-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                        <Text style={styles.navText}>Activity</Text>
                      </View>
                      {authChecked && !isAuthed && (
                        <Ionicons name="lock-closed-outline" size={16} color="#FDF05D" style={styles.lockIcon} />
                      )}
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => navigation.navigate('BusinessAccountPage')}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons name="person-circle-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                        <Text style={styles.navText}>Account</Text>
                      </View>
                    </View>
                  </TouchableOpacity>
                </View>

                <View style={styles.sideNavSection}>
                  <TouchableOpacity
                    style={styles.peerMapCard}
                    activeOpacity={0.9}
                    onPress={() => openInSameTab('/venues')}
                  >
                    <LinearGradient
                      colors={['rgba(255,255,255,0.08)', 'rgba(0,0,0,0.4)']}
                      style={styles.peerMapInner}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                    >
                      <View style={styles.peerMapContent}>
                        <Ionicons name="map-outline" size={18} color="#FDF05D" style={{ marginRight: 8 }} />
                        <View style={{ flex: 1 }}>
                          <Text style={styles.peerMapTitle}>Peer Map</Text>
                          <Text style={styles.peerMapSubtitle}>View 100+ venues on Collabbay</Text>
                        </View>
                      </View>
                    </LinearGradient>
                  </TouchableOpacity>

                  {!isSubscriber && (
                    <TouchableOpacity
                      style={styles.subscribeCard}
                      activeOpacity={0.9}
                      onPress={() => openInSameTab('https://collabbayadmin.com/subscribe')}
                    >
                      <LinearGradient
                        colors={['#FDF05D', '#fbbf24']}
                        style={styles.subscribeGradient}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                      >
                        <View style={styles.subscribeContent}>
                          <Ionicons name="rocket-outline" size={20} color="#000" style={{ marginRight: 8 }} />
                          <View style={{ flex: 1 }}>
                            <Text style={styles.subscribeTitle}>Subscribe & unlock</Text>
                            <Text style={styles.subscribeSubtitle}>Unlock Activity & Calendar, plus more.</Text>
                          </View>
                        </View>
                      </LinearGradient>
                    </TouchableOpacity>
                  )}
                </View>

                <View style={styles.sideNavBottom}>
                  <TouchableOpacity
                    style={styles.bottomLink}
                    onPress={() => openInSameTab('https://collabbayadmin.com/FAQs')}
                  >
                    <Ionicons name="help-circle-outline" size={18} color="#f5f5f5" style={{ marginRight: 8 }} />
                    <View>
                      <Text style={styles.bottomLinkText}>FAQs</Text>
                      <Text style={styles.bottomLinkCaption}>Common questions answered</Text>
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.bottomLink}
                    onPress={() => Linking.openURL('mailto:info@collab-bay.com')}
                  >
                    <Ionicons name="headset-outline" size={18} color="#f5f5f5" style={{ marginRight: 8 }} />
                    <View>
                      <Text style={styles.bottomLinkText}>Help</Text>
                      <Text style={styles.bottomLinkCaption}>We&apos;re here 24/7</Text>
                    </View>
                  </TouchableOpacity>
                </View>
              </LinearGradient>
            </View>

            <View style={styles.mainArea}>
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="black" />
                <Text style={styles.loadingText}>Loading your offers…</Text>
              </View>
            </View>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  // MAIN RENDER (NOW WITH SIDENAV)
  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.appShell}>
        <View style={styles.shellRow}>
          {/* ✅ SIDENAV */}
          <View style={styles.sideNav}>
            <LinearGradient
              colors={['#050509', '#050505', '#000000']}
              style={styles.sideNavInner}
            >
              <LinearGradient
                colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
                style={styles.sideNavGlow}
                start={{ x: 0.1, y: 0 }}
                end={{ x: 1, y: 1 }}
              />

              <View style={styles.sideNavHeader}>
                <Image
                  source={{ uri: 'https://collabbayadmin.com/CollabbayLogoDark.png' }}
                  style={styles.sideNavLogoImage}
                  resizeMode="contain"
                />
                <View>
                  <Text style={styles.logoWordmark}>
                    <Text style={{ fontWeight: '300' }}>collab</Text>
                    bay
                    <Text style={styles.logoDot}>.</Text>
                  </Text>
                  <Text style={styles.logoDesktopTag}>DESKTOP</Text>
                </View>
              </View>

              <View style={styles.sideNavSection}>
                <LinearGradient
                  colors={['#111111', '#2b2b2b']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={styles.usernamePill}
                >
                  <Text numberOfLines={1} ellipsizeMode="tail" style={styles.usernamePillText}>
                    {userData.Username ? ` ${userData.Username}` : 'Welcome Back'}
                  </Text>
                </LinearGradient>

                <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate('MyPanel')}>
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons name="home-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                      <Text style={styles.navText}>Home</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate('MyOffers')}>
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons name="pricetags-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                      <Text style={styles.navText}>My Offers</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) openInSameTab('https://collabbayadmin.com/subscribe');
                    else navigation.navigate('MyCalendar');
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons name="calendar-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                      <Text style={styles.navText}>My Calendar</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons name="lock-closed-outline" size={16} color="#FDF05D" style={styles.lockIcon} />
                    )}
                  </View>
                </TouchableOpacity>

                <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate('BrowseCreators')}>
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons name="people-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                      <Text style={styles.navText}>Browse Creators</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) openInSameTab('https://collabbayadmin.com/subscribe');
                    else navigation.navigate('MyActivityFeed');
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons name="chatbubbles-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                      <Text style={styles.navText}>Activity</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons name="lock-closed-outline" size={16} color="#FDF05D" style={styles.lockIcon} />
                    )}
                  </View>
                </TouchableOpacity>

                <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate('BusinessAccountPage')}>
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons name="person-circle-outline" size={20} color="#f5f5f5" style={styles.navIcon} />
                      <Text style={styles.navText}>Account</Text>
                    </View>
                  </View>
                </TouchableOpacity>
              </View>

              <View style={styles.sideNavSection}>
                <TouchableOpacity style={styles.peerMapCard} activeOpacity={0.9} onPress={() => openInSameTab('/venues')}>
                  <LinearGradient
                    colors={['rgba(255,255,255,0.08)', 'rgba(0,0,0,0.4)']}
                    style={styles.peerMapInner}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                  >
                    <View style={styles.peerMapContent}>
                      <Ionicons name="map-outline" size={18} color="#FDF05D" style={{ marginRight: 8 }} />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.peerMapTitle}>Peer Map</Text>
                        <Text style={styles.peerMapSubtitle}>View 100+ venues on Collabbay</Text>
                      </View>
                    </View>
                  </LinearGradient>
                </TouchableOpacity>

                {!isSubscriber && (
                  <TouchableOpacity
                    style={styles.subscribeCard}
                    activeOpacity={0.9}
                    onPress={() => openInSameTab('https://collabbayadmin.com/subscribe')}
                  >
                    <LinearGradient
                      colors={['#FDF05D', '#fbbf24']}
                      style={styles.subscribeGradient}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                    >
                      <View style={styles.subscribeContent}>
                        <Ionicons name="rocket-outline" size={20} color="#000" style={{ marginRight: 8 }} />
                        <View style={{ flex: 1 }}>
                          <Text style={styles.subscribeTitle}>Subscribe & unlock</Text>
                          <Text style={styles.subscribeSubtitle}>Unlock Activity & Calendar, plus more.</Text>
                        </View>
                      </View>
                    </LinearGradient>
                  </TouchableOpacity>
                )}
              </View>

              <View style={styles.sideNavBottom}>
                <TouchableOpacity style={styles.bottomLink} onPress={() => openInSameTab('https://collabbayadmin.com/FAQs')}>
                  <Ionicons name="help-circle-outline" size={18} color="#f5f5f5" style={{ marginRight: 8 }} />
                  <View>
                    <Text style={styles.bottomLinkText}>FAQs</Text>
                    <Text style={styles.bottomLinkCaption}>Common questions answered</Text>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity style={styles.bottomLink} onPress={() => Linking.openURL('mailto:info@collab-bay.com')}>
                  <Ionicons name="headset-outline" size={18} color="#f5f5f5" style={{ marginRight: 8 }} />
                  <View>
                    <Text style={styles.bottomLinkText}>Help</Text>
                    <Text style={styles.bottomLinkCaption}>We&apos;re here 24/7</Text>
                  </View>
                </TouchableOpacity>
              </View>
            </LinearGradient>
          </View>

          {/* ✅ MAIN AREA */}
          <View style={styles.mainArea}>
            <View style={styles.headerBar}>
              <View style={styles.headerLeft}>
                <TouchableOpacity style={styles.headerBackButton} onPress={handleBackPress}>
                  <Ionicons name="arrow-back" size={20} color="black" />
                </TouchableOpacity>
                <View style={styles.headerTitles}>
                  <Text style={styles.pageTitle}>My offers</Text>
                  <Text style={styles.pageSubtitle}>
                    Quickly jump into booking requests and keep your offer cards looking sharp.
                  </Text>
                </View>
              </View>

              <View style={styles.headerRight}>
                {userData?.Username ? (
                  <View style={styles.userBadge}>
                    <View style={styles.userBadgeInitial}>
                      <Text style={styles.userBadgeInitialText}>
                        {userData.Username.charAt(0).toUpperCase()}
                      </Text>
                    </View>
                    <Text style={styles.userBadgeText} numberOfLines={1}>
                      {userData.Username}
                    </Text>
                  </View>
                ) : null}
              </View>
            </View>

            <View style={styles.searchRow}>
              <View style={styles.searchWrapper}>
                <Ionicons name="search-outline" size={18} color="#9ca3af" style={styles.searchIcon} />
                <TextInput
                  style={styles.searchInput}
                  placeholder="Search my campaigns"
                  placeholderTextColor="#9ca3af"
                  value={searchQuery}
                  onChangeText={handleSearchChange}
                />
              </View>

              <TouchableOpacity
                style={styles.newOfferButton}
                onPress={() => navigation.navigate('GuidedOfferSetup')}
              >
                <Ionicons name="add" size={16} color="#111827" style={{ marginRight: 6 }} />
                <Text style={styles.newOfferButtonText}>New Offer</Text>
              </TouchableOpacity>
            </View>

            <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
              {/* RAG SECTION */}
              <View style={styles.section}>
                {loadingRAG ? (
                  <View style={styles.ragLoadingRow}>
                    <ActivityIndicator size="small" color="black" />
                    <Text style={styles.ragLoadingText}>Checking content status…</Text>
                  </View>
                ) : adminRAGData.length === 0 ? null : (
                  <View style={styles.ragSectionCard}>
                    <View style={styles.sectionHeaderRow}>
                      <View style={styles.sectionHeaderLeft}>
                        <Ionicons name="time-outline" size={18} color="#111827" style={{ marginRight: 6 }} />
                        <Text style={styles.sectionLabel}>Content status</Text>
                      </View>
                      <View style={styles.sectionMetaPill}>
                        <Text style={styles.sectionMetaPillText}>
                          {adminRAGData.length} collab{adminRAGData.length > 1 ? 's' : ''} need content
                        </Text>
                      </View>
                    </View>

                    {adminRAGData.map((collab, index) => {
                      const dueDate = new Date(collab.LatestDeliveryDate);
                      const today = new Date();
                      const daysLeft = differenceInCalendarDays(dueDate, today);

                      const isYellow = collab.Status === 'YELLOW';
                      const statusColor = isYellow ? '#f2c94c' : '#eb5757';

                      return (
                        <View
                          key={index}
                          style={[
                            styles.ragCard,
                            { borderColor: statusColor },
                            index > 0 && { marginTop: 8 },
                          ]}
                        >
                          <View style={styles.ragCardTopRow}>
                            <View style={[styles.ragBadge, isYellow ? styles.ragBadgeYellow : styles.ragBadgeRed]}>
                              <Text style={styles.ragBadgeText}>{isYellow ? 'Due soon' : 'Overdue'}</Text>
                            </View>
                            <Text style={styles.ragCollabMeta}>
                              Latest delivery: {formatDate(collab.LatestDeliveryDate)}
                            </Text>
                          </View>

                          <Text style={styles.ragTitle}>
                            {isYellow
                              ? `Pending content, due in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`
                              : 'Content overdue.'}
                          </Text>
                          <Text style={styles.ragSubtitle}>
                            from <Text style={styles.ragPromoterName}>{collab.Promoter}</Text>
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                )}
              </View>

              {/* MY OFFERS */}
              <View style={styles.section}>
                <View style={styles.sectionHeaderRow}>
                  <View style={styles.sectionHeaderLeft}>
                    <Ionicons name="pricetag-outline" size={18} color="#111827" style={{ marginRight: 6 }} />
                    <Text style={styles.sectionLabel}>My offers</Text>
                  </View>
                  {filteredData.length > 0 && !noOffersMessage ? (
                    <Text style={styles.sectionMeta}>
                      {filteredData.length} campaign{filteredData.length > 1 ? 's' : ''}
                    </Text>
                  ) : null}
                </View>

                {noOffersMessage ? (
                  <TouchableOpacity
                    onPress={() => navigation.navigate('GuidedOfferSetup')}
                    style={styles.emptyOffersCard}
                  >
                    <View style={styles.emptyIconCircle}>
                      <Ionicons name="sparkles-outline" size={22} color="#111827" />
                    </View>
                    <Text style={styles.emptyOffersTitle}>No offers yet</Text>
                    <Text style={styles.emptyOffersSubtitle}>
                      Start by posting your first collab offer — we&apos;ll help match the right creators.
                    </Text>
                    <View style={styles.emptyOffersCta}>
                      <Ionicons name="add" size={16} color="#111827" style={{ marginRight: 6 }} />
                      <Text style={styles.emptyOffersCtaText}>Post an offer</Text>
                    </View>
                  </TouchableOpacity>
                ) : filteredData.length > 0 ? (
                  filteredData.map((item, index) => {
                    const listingID = item.ListingID;
                    const imageUploadUrl = `https://collabbayadmin.com/ListingImageUpload.html?ListingID=${encodeURIComponent(
                      listingID
                    )}`;

                    const mainOfferText =
                      (item.NonCreditOffer && item.NonCreditOffer.trim()) ||
                      item.AdHeader ||
                      'Collab offer';

                    return (
                      <View style={styles.card} key={index}>
                        <View style={styles.cardTopBar}>
                          <View style={styles.cardTopLeft}>
                            {item.PauseLiveToggle === 'Live' ? (
                              <View style={styles.liveCard}>
                                <View style={styles.liveDot} />
                                <Text style={styles.liveText}>Live</Text>
                              </View>
                            ) : (
                              <View style={styles.pausedCard}>
                                <Ionicons name="pause" size={10} color="#f9fafb" style={{ marginRight: 4 }} />
                                <Text style={styles.pausedText}>Paused</Text>
                              </View>
                            )}

                            {item.OnBehalfOf && item.OnBehalfOf.trim() !== '' && (
                              <View style={styles.clientPill}>
                                <Ionicons name="briefcase-outline" size={13} color="#111827" style={{ marginRight: 4 }} />
                                <Text style={styles.clientPillText}>{item.OnBehalfOf}</Text>
                              </View>
                            )}
                          </View>

                          <Text style={styles.createdText}>Created {formatDate(item.Created_At)}</Text>
                        </View>

                        <View style={styles.cardInnerRow}>
                          {/* LEFT: IMAGE + OVERLAY EDIT HINT */}
                          <View style={styles.cardImageColumn}>
                            <TouchableOpacity
                              style={styles.bannerTapArea}
                              onPress={() => Linking.openURL(imageUploadUrl)}
                              activeOpacity={0.9}
                            >
                              {/* ✅ Overlay badge (top-right) to make “editable” obvious */}
                              <View style={styles.imageEditOverlay}>
                                <Ionicons name="create-outline" size={14} color="#111827" />
                                <Text style={styles.imageEditOverlayText}>Edit</Text>
                              </View>

                              {hasAnyImage(item.LogoImage) ? (
                                <View style={styles.bannerContainer}>
                                  <Image style={styles.bannerImage} source={{ uri: getCoverImage(item.LogoImage) }} />
                                </View>
                              ) : (
                                <View style={styles.imagePlaceholder}>
                                  <Ionicons name="image-outline" size={26} color="#4b5563" style={{ marginBottom: 4 }} />
                                  <Text style={styles.imagePlaceholderText}>No images yet</Text>
                                </View>
                              )}
                            </TouchableOpacity>

                            {/* (kept) existing inline image edit button */}
                            <TouchableOpacity
                              style={styles.editImageInlineButton}
                              onPress={() => Linking.openURL(imageUploadUrl)}
                            >
                              <Ionicons name="create-outline" size={14} color="#111827" style={{ marginRight: 4 }} />
                              <Text style={styles.editImageInlineText}>Edit listing images</Text>
                            </TouchableOpacity>
                          </View>

                          {/* MIDDLE: OFFER INFO (tightened spacing a bit) */}
                          <View style={styles.cardBodyColumn}>
                            <Text style={styles.mainOfferLabel}>What you&apos;re gifting</Text>

                            <Text style={styles.mainOfferText}>
                              {mainOfferText.length > 80 ? `${mainOfferText.substring(0, 80)}…` : mainOfferText}
                            </Text>

                            <View style={styles.locationRow}>
                              <Ionicons name="location-outline" size={13} color="#6b7280" style={{ marginRight: 4 }} />
                              <Text style={styles.cardSubLocationText}>{item.Location}</Text>
                            </View>

                            <Text style={styles.cardHintText}>
                              Keep this clear so staff and creators instantly know what&apos;s included.
                            </Text>
                          </View>

                          {/* RIGHT: CTAs (add Edit listing button) */}
                          <View style={styles.cardActionsColumn}>
                            <TouchableOpacity
                              style={[styles.cardActionButton, styles.cardActionPrimary]}
                              onPress={() =>
                                navigation.navigate('RequestList', {
                                  ListingID: item.ListingID,
                                })
                              }
                            >
                              <Ionicons name="people-outline" size={21} color="#111827" style={{ marginRight: 6 }} />
                              <Text style={styles.cardActionPrimaryText}>View booking requests</Text>
                              <Ionicons name="chevron-forward" size={20} color="#111827" style={{ marginLeft: 4 }} />
                            </TouchableOpacity>

                            {/* ✅ NEW: Edit listing (goes to EditMyListing) */}
                            <TouchableOpacity
                              style={[styles.cardActionButton, styles.cardActionSecondary]}
                              onPress={() =>
                                navigation.navigate('EditMyListing', {
                                  ListingID: item.ListingID,
                                })
                              }
                              activeOpacity={0.9}
                            >
                              <Ionicons name="create-outline" size={18} color="#111827" style={{ marginRight: 6 }} />
                              <Text style={styles.cardActionSecondaryText}>Edit listing</Text>
                              <Ionicons name="chevron-forward" size={18} color="#111827" style={{ marginLeft: 6 }} />
                            </TouchableOpacity>
                          </View>
                        </View>
                      </View>
                    );
                  })
                ) : (
                  <View style={styles.noSearchResultsCard}>
                    <Ionicons name="search-outline" size={20} color="#6b7280" style={{ marginBottom: 6 }} />
                    <Text style={styles.noOffersText}>No campaigns match your search.</Text>
                    <Text style={styles.noOffersSubText}>Try a different keyword or clear the search box.</Text>
                  </View>
                )}
              </View>
            </ScrollView>
          </View>
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  appShell: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  shellRow: {
    flex: 1,
    flexDirection: 'row',
  },

  // SIDE NAV
  sideNav: {
    width: 250,
    backgroundColor: '#000',
  },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  sideNavGlow: {
    position: 'absolute',
    top: 140,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    opacity: 0.55,
  },
  sideNavHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26,
    zIndex: 2,
  },
  sideNavLogoImage: {
    width: 38,
    height: 38,
    marginRight: 10,
  },
  logoWordmark: {
    fontSize: 16,
    color: '#f5f5f5',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  logoDot: {
    color: '#FDF05D',
    fontSize: 18,
    fontWeight: '800',
  },
  logoDesktopTag: {
    fontSize: 9,
    color: '#b3b3b3',
    letterSpacing: 2,
    marginTop: 2,
  },
  sideNavSection: {
    marginTop: 12,
    marginBottom: 20,
    zIndex: 2,
  },
  usernamePill: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    marginLeft: 1,
    marginBottom: 10,
  },
  usernamePillText: {
    fontSize: 8,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 1,
    textAlign: 'left',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 6,
  },
  navIcon: {
    marginRight: 10,
  },
  navItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  navItemMainLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  navText: {
    color: '#f5f5f5',
    fontSize: 14,
    fontWeight: '500',
  },
  lockIcon: {
    marginLeft: 6,
  },

  peerMapCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 4,
    marginBottom: 10,
  },
  peerMapInner: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  peerMapContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  peerMapTitle: {
    color: '#f5f5f5',
    fontSize: 13,
    fontWeight: '600',
  },
  peerMapSubtitle: {
    color: '#b3b3b3',
    fontSize: 11,
    marginTop: 2,
  },

  subscribeCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 10,
  },
  subscribeGradient: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  subscribeContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  subscribeTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
  },
  subscribeSubtitle: {
    fontSize: 11,
    color: '#1f2933',
    marginTop: 2,
  },

  sideNavBottom: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#181818',
    zIndex: 2,
  },
  bottomLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  bottomLinkText: {
    fontSize: 13,
    color: '#f5f5f5',
    fontWeight: '600',
  },
  bottomLinkCaption: {
    fontSize: 11,
    color: '#b3b3b3',
    marginTop: 2,
  },

  /* MAIN AREA */
  mainArea: {
    flex: 1,
    paddingHorizontal: 18,
    paddingVertical: 14,
    backgroundColor: '#e5e7eb',
  },
  headerBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  headerBackButton: {
    width: 32,
    height: 32,
    borderRadius: 999,
    backgroundColor: '#e5e7eb',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  headerTitles: {
    flexShrink: 1,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
  },
  pageSubtitle: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },
  headerRight: {
    marginLeft: 10,
  },
  userBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#111827',
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  userBadgeInitial: {
    width: 22,
    height: 22,
    borderRadius: 999,
    backgroundColor: '#fdf05d',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
  },
  userBadgeInitialText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#111827',
  },
  userBadgeText: {
    fontSize: 11,
    color: '#f9fafb',
    maxWidth: 120,
  },

  /* SEARCH ROW */
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    marginTop: 6,
  },
  searchWrapper: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  searchIcon: {
    marginRight: 6,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#111827',
    paddingVertical: 4,
  },
  newOfferButton: {
    marginLeft: 8,
    backgroundColor: '#fdf05d',
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 7,
    flexDirection: 'row',
    alignItems: 'center',
  },
  newOfferButtonText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#111827',
  },

  scrollContent: {
    paddingBottom: 40,
  },

  /* SECTIONS */
  section: {
    marginBottom: 18,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  sectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionLabel: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
  },
  sectionMeta: {
    fontSize: 12,
    color: '#6b7280',
  },
  sectionMetaPill: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: '#111827',
  },
  sectionMetaPillText: {
    fontSize: 11,
    color: '#f9fafb',
    fontWeight: '600',
  },

  /* RAG SECTION */
  ragSectionCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 14,
    padding: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  ragLoadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  ragLoadingText: {
    marginLeft: 8,
    fontSize: 12,
    color: '#6b7280',
  },
  ragCard: {
    borderRadius: 10,
    borderWidth: 1.5,
    backgroundColor: '#fefefe',
    padding: 10,
  },
  ragCardTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  ragBadge: {
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  ragBadgeYellow: {
    backgroundColor: '#fef3c7',
  },
  ragBadgeRed: {
    backgroundColor: '#fee2e2',
  },
  ragBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#111827',
  },
  ragCollabMeta: {
    fontSize: 11,
    color: '#6b7280',
  },
  ragTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 2,
  },
  ragSubtitle: {
    fontSize: 12,
    color: '#4b5563',
  },
  ragPromoterName: {
    fontWeight: '600',
  },

  /* CARDS (OFFERS) */
  card: {
    width: '100%',
    backgroundColor: '#ffffff',
    borderRadius: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#d1d5db',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.06,
    shadowRadius: 5,
    elevation: 2,
    overflow: 'hidden',
  },
  cardTopBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  cardTopLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardInnerRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    minHeight: 215, // slightly tighter
    paddingHorizontal: 10,
    paddingVertical: 10, // slightly tighter
  },

  /* LEFT: IMAGE COLUMN */
  cardImageColumn: {
    flex: 1.8,
    marginRight: 10,
  },
  bannerTapArea: {
    flex: 1,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#e5e7eb',
    position: 'relative',
  },
  imageEditOverlay: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 5,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(253,240,93,0.92)',
    borderRadius: 999,
    paddingVertical: 5,
    paddingHorizontal: 9,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.12)',
  },
  imageEditOverlayText: {
    marginLeft: 6,
    fontSize: 12,
    fontWeight: '800',
    color: '#111827',
  },
  bannerContainer: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  bannerImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  imagePlaceholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e5e7eb',
  },
  imagePlaceholderText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#4b5563',
  },
  editImageInlineButton: {
    marginTop: 10,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: '#fdf05d',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  editImageInlineText: {
    fontSize: 12,
    fontWeight: '600',
    color: 'black',
  },

  /* MIDDLE: INFO COLUMN (tightened spacing) */
  cardBodyColumn: {
    flex: 1.7,
    paddingHorizontal: 6,
    justifyContent: 'flex-start',
    paddingTop: 2,
  },
  mainOfferLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: '#6b7280',
    marginBottom: 2,
  },
  mainOfferText: {
    fontSize: 16,
    fontWeight: '800',
    color: '#111827',
    marginBottom: 6,
    lineHeight: 20,
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  cardSubLocationText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#4b5563',
  },
  cardHintText: {
    fontSize: 11,
    color: '#9ca3af',
    lineHeight: 16,
    maxWidth: 420,
  },

  clientPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
    marginLeft: 8,
  },
  clientPillText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#111827',
  },

  /* RIGHT: CTA COLUMN */
  cardActionsColumn: {
    flex: 1,
    justifyContent: 'center',
    marginLeft: 10,
  },
  cardActionButton: {
    borderRadius: 25,
    paddingVertical: 9,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardActionPrimary: {
    backgroundColor: '#fdf05d',
    borderWidth: 1,
    borderColor: '#ead43b',
  },
  cardActionPrimaryText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
    textAlign: 'center',
    paddingHorizontal: 6,
  },

  // ✅ NEW secondary button (Edit listing)
  cardActionSecondary: {
    marginTop: 10,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
  },
  cardActionSecondaryText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
  },

  /* STATUS PILLS */
  liveCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#d1fae5',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: 999,
    backgroundColor: '#16a34a',
    marginRight: 5,
  },
  liveText: {
    fontWeight: '700',
    color: '#15803d',
    fontSize: 11,
  },
  pausedCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#111827',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  pausedText: {
    fontWeight: '600',
    color: '#f9fafb',
    fontSize: 11,
  },
  createdText: {
    fontSize: 11,
    color: '#6b7280',
  },

  /* EMPTY STATES */
  emptyOffersCard: {
    backgroundColor: '#fefce8',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#facc15',
    alignItems: 'flex-start',
  },
  emptyIconCircle: {
    width: 30,
    height: 30,
    borderRadius: 999,
    backgroundColor: '#fef9c3',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  emptyOffersTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  emptyOffersSubtitle: {
    fontSize: 12,
    color: '#4b5563',
    marginBottom: 10,
  },
  emptyOffersCta: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fdf05d',
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  emptyOffersCtaText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#111827',
  },
  noSearchResultsCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 14,
    alignItems: 'center',
  },
  noOffersText: {
    fontSize: 13,
    color: '#4b5563',
    textAlign: 'center',
  },
  noOffersSubText: {
    marginTop: 2,
    fontSize: 11,
    color: '#9ca3af',
    textAlign: 'center',
  },

  /* GENERIC */
  loadingContainer: {
    flex: 1,
    backgroundColor: '#e5e7eb',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 8,
    fontSize: 13,
    color: '#6b7280',
  },
  spinner: {
    marginVertical: 10,
    alignSelf: 'center',
    color: 'black',
  },
});

export default PotentialCollabsDashboardView;
